import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { RequireAuth } from "$routes/_lib/require-auth";
import InitiateStockTakeModal from "$routes/inventory/_lib/initiate-stock-take-modal";
import {
  ALLOWED_INVENTORY_REQUEST_ROLES,
  ALLOWED_STOCKTAKE_HISTORY_ROLES,
  ALLOWED_STOCKTAKE_ROLES,
  ALLOWED_UPDATE_LOT_ROLES,
} from "$routes/inventory/_lib/permissions";
import InventoryAudit from "$routes/inventory/audit/page";
import { BulkReceivePage } from "$routes/inventory/bulk-receive/page";
import { BulkStocktakePage } from "$routes/inventory/bulk-stocktake/page";
import FulfillMoveRequest from "$routes/inventory/move-request/[:moveRequestId]/fulfill/page";
import ActionedMoveRequestOverview from "$routes/inventory/move-request/[:moveRequestId]/overview/page";
import MoveRequestNew from "$routes/inventory/move-requests/new/page";
import MoveRequests from "$routes/inventory/move-requests/page";
import InventoryManagementOverview from "$routes/inventory/page";
import StockTake from "$routes/inventory/stocktake/[:stockTakeId]/page";
import StockTakeReview from "$routes/inventory/stocktake/[:stockTakeId]/review/page";
import HistoricStockTakeOverview from "$routes/inventory/stocktake-history/[:stockTakeId]/page";
import StockTakeHistory from "$routes/inventory/stocktake-history/page";
import { Role } from "elephant-web/gql";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  linkToBulkReceive,
  linkToBulkStocktake,
  linkToFulfillInventoryMoveRequest,
  linkToHistoricStockTakeOverview,
  linkToInitiateStockTake,
  linkToInventory,
  linkToInventoryAudit,
  linkToInventoryMoveRequestNew,
  linkToInventoryMoveRequestOverview,
  linkToInventoryMoveRequests,
  linkToStockTake,
  linkToStockTakeHistory,
  linkToStockTakeReview,
} from "./links";

const BulkReceiveRoute = () => {
  const [bulkReceiveFlag, { loading }] = useFeatureFlag(
    featureFlags.inventory.bulkReceive,
  );

  if (loading) {
    return null;
  }

  if (!bulkReceiveFlag) {
    return <Navigate to={linkToInventory()} replace />;
  }

  return <BulkReceivePage />;
};

const BulkStocktakeRoute = () => {
  const [bulkStocktakeFlag, { loading }] = useFeatureFlag(
    featureFlags.inventory.bulkStocktake,
  );

  if (loading) {
    return null;
  }

  if (!bulkStocktakeFlag) {
    return <Navigate to={linkToInventory()} replace />;
  }

  return <BulkStocktakePage />;
};

// React router v6 relies on relative routing so to make the routing in this file work we
// need to strip the leading path off. When using a nested "Routes" absolute routing is
// not yet supported:
// https://github.com/remix-run/react-router/issues/8035#issuecomment-997737565
const removeInventory = (pathname: string) =>
  pathname.replace("/inventory", "");

export const InventoryRoutes = () => {
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(useLocation().search);

  return (
    <Routes>
      {/* Requests */}
      <Route
        path={removeInventory(linkToInventoryMoveRequestNew())}
        element={
          <RequireAuth only={ALLOWED_INVENTORY_REQUEST_ROLES}>
            <MoveRequestNew />
          </RequireAuth>
        }
      />
      <Route
        path={removeInventory(linkToInventoryMoveRequests())}
        element={
          <RequireAuth only={ALLOWED_INVENTORY_REQUEST_ROLES}>
            <MoveRequests />
          </RequireAuth>
        }
      />
      <Route
        path={removeInventory(linkToFulfillInventoryMoveRequest())}
        element={
          <RequireAuth only={[Role.Pharmacist]}>
            <FulfillMoveRequest />
          </RequireAuth>
        }
      />
      <Route
        path={removeInventory(linkToInventoryMoveRequestOverview())}
        element={
          <RequireAuth only={ALLOWED_INVENTORY_REQUEST_ROLES}>
            <ActionedMoveRequestOverview />
          </RequireAuth>
        }
      />
      {/* Stocktake */}
      <Route
        path={removeInventory(linkToStockTakeReview())}
        element={
          <RequireAuth only={ALLOWED_STOCKTAKE_ROLES}>
            <StockTakeReview />
          </RequireAuth>
        }
      />
      <Route
        path={removeInventory(linkToStockTake())}
        element={
          <RequireAuth only={ALLOWED_STOCKTAKE_ROLES}>
            <StockTake />
          </RequireAuth>
        }
      />
      <Route
        path={removeInventory(linkToHistoricStockTakeOverview())}
        element={
          <RequireAuth only={ALLOWED_STOCKTAKE_HISTORY_ROLES}>
            <HistoricStockTakeOverview />
          </RequireAuth>
        }
      />
      <Route
        path={removeInventory(linkToStockTakeHistory())}
        element={
          <RequireAuth only={ALLOWED_STOCKTAKE_HISTORY_ROLES}>
            <StockTakeHistory />
          </RequireAuth>
        }
      />
      {/* Receive */}
      <Route
        path={removeInventory(linkToBulkReceive())}
        element={
          <RequireAuth only={ALLOWED_UPDATE_LOT_ROLES}>
            <BulkReceiveRoute />
          </RequireAuth>
        }
      />
      {/* Stocktake (bulk) */}
      <Route
        path={removeInventory(linkToBulkStocktake())}
        element={
          <RequireAuth only={ALLOWED_STOCKTAKE_ROLES}>
            <BulkStocktakeRoute />
          </RequireAuth>
        }
      />
      {/* Rest */}
      <Route
        path={removeInventory(linkToInventoryAudit())}
        element={
          <RequireAuth only={[Role.Pharmacist, Role.Clinic]}>
            <InventoryAudit />
          </RequireAuth>
        }
      />
      <Route
        path={removeInventory(linkToInventory())}
        element={
          <RequireAuth>
            <InventoryManagementOverview />
            <Outlet />
          </RequireAuth>
        }
      >
        <Route
          path={removeInventory(linkToInitiateStockTake())}
          element={
            <RequireAuth only={ALLOWED_STOCKTAKE_ROLES}>
              <InitiateStockTakeModal
                open
                onClose={() => navigate(linkToInventory())}
                locationId={searchParams.get("locationId")}
              />
            </RequireAuth>
          }
        />
      </Route>
    </Routes>
  );
};
